import { reactive, ref, type Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { getWordList } from "@/common/graphql/expression";
import { getChainInfo, getChainList } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import { getCityByPrefGroupedByStepA } from "@/common/graphql/cityByPref";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetAuthType,
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetGroup,
  localStorageGetPersonalInfo,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { GeneralConstant, PossibleType } from "@/common/constant";
import { checkChainListDisplay } from "@/common/business";
import type {
  ChainSelectArray,
  DeliveryAreaFilterArray,
  KanaStepA,
  ShopSelectArray,
} from "../types";
import {
  GetChainDocument,
  GetChainQuery,
  ListDeliveryAreaDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ShopListInput,
} from "@/generated/graphql";
import shopStore from "@/store/shopStore";

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestInitContext = () => {
  const wordList = reactive<Record<string, string>>({});
  const chainViewFlag = checkChainListDisplay(localStorageGetAuthType());
  return { wordList, chainViewFlag };
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestInitState = () => {
  const unitItems = ref<number[]>([]);
  const paymentRequiredFlg = ref(false);
  const paymentList = ref<
    | Array<{
      __typename?: "ChainPaymentMethod" | undefined;
      chain_code: string;
      created_date: string;
      created_user_id: string;
      customer_code: string;
      payment_amount_required: string;
      payment_method: string;
      sort_order: number;
      updated_date?: string | null | undefined;
      updated_user_id?: string | null | undefined;
    } | null | undefined>
    | null
    | undefined
  >([]);
  const isDisabled = ref(false);
  const isPersonalInfoPossible = ref(false);
  const personalInfoImpossible = ref("");
  const house_card_display = ref(0);
  const house_card_validation = ref("");
  const house_card_required = ref(0);
  const house_card_change = ref(false);
  const house_card_befor = ref("");
  const register_no_max_length = ref(0);
  const register_no_validation = ref("");
  const receipt_no_max_length = ref(0);
  const receipt_no_validation = ref("");
  const memberNotesBefore = ref("");
  const deliveryNotesBefore = ref("");
  const messageMemberNotes = ref("");
  const messageDeliveryNotes = ref("");
  const authType = ref("");
  const shipping_label_print_flg = ref(false);
  const delivery_order_print_flg = ref(false);
  const is_delivery_address1_display = ref(0);
  const is_hide_delivery_time_on_spot = ref(0);

  return {
    unitItems,
    paymentRequiredFlg,
    paymentList,
    isDisabled,
    isPersonalInfoPossible,
    personalInfoImpossible,
    house_card_display,
    house_card_validation,
    house_card_required,
    house_card_change,
    house_card_befor,
    register_no_max_length,
    register_no_validation,
    receipt_no_max_length,
    receipt_no_validation,
    memberNotesBefore,
    deliveryNotesBefore,
    messageMemberNotes,
    messageDeliveryNotes,
    authType,
    shipping_label_print_flg,
    delivery_order_print_flg,
    is_delivery_address1_display,
    is_hide_delivery_time_on_spot,
  };
};

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliveryRequestInitParams {
  initializeTableHeaders: (pageKey: string) => void;
  pageKey: string;
  chainViewFlag: boolean;
  authType: Ref<string>;
  isPersonalInfoPossible: Ref<boolean>;
  personalInfoImpossible: Ref<string>;
  wordList: Record<string, string>;
  bufChainListItems: Ref<ChainSelectArray>;
  chainListItems: Ref<ChainSelectArray>;
  chainAllCheckFlag: Ref<string>;
  chainSelectValues: Ref<ChainSelectArray>;
  searchConditionChainCode: Ref<(string | null | undefined)[] | null | undefined>;
  shopListInput: ShopListInput;
  bufShopListItems: Ref<ShopSelectArray>;
  shopAllCheckFlag: Ref<string>;
  shopDisabledFlag: Ref<boolean>;
  shopSelectValues: Ref<{ shop_code: string; name: string }[]>;
  searchConditionShopCode: Ref<(string | null | undefined)[] | null | undefined>;
  shopListItems: Ref<ShopSelectArray>;
  areaDisabledFlag: Ref<boolean>;
  shipping_label_print_flg: Ref<boolean>;
  delivery_order_print_flg: Ref<boolean>;
  house_card_display: Ref<number>;
  house_card_validation: Ref<string>;
  house_card_required: Ref<number>;
  house_card_change: Ref<boolean>;
  house_card_befor: Ref<string>;
  register_no_max_length: Ref<number>;
  receipt_no_max_length: Ref<number>;
  register_no_validation: Ref<string>;
  receipt_no_validation: Ref<string>;
  memberNotesBefore: Ref<string>;
  deliveryNotesBefore: Ref<string>;
  messageMemberNotes: Ref<string>;
  messageDeliveryNotes: Ref<string>;
  is_delivery_address1_display: Ref<number>;
  is_hide_delivery_time_on_spot: Ref<number>;
  areaListItems: Ref<DeliveryAreaFilterArray>;
  unitItems: Ref<number[]>;
  originalAddress: Ref<string>;
  prefSelectValue: Ref<string>;
  cityBlockList: Ref<KanaStepA | null | undefined>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestInit = ({
  initializeTableHeaders,
  pageKey,
  chainViewFlag,
  authType,
  isPersonalInfoPossible,
  personalInfoImpossible,
  wordList,
  bufChainListItems,
  chainListItems,
  chainAllCheckFlag,
  chainSelectValues,
  searchConditionChainCode,
  shopListInput,
  bufShopListItems,
  shopAllCheckFlag,
  shopDisabledFlag,
  shopSelectValues,
  searchConditionShopCode,
  shopListItems,
  areaDisabledFlag,
  shipping_label_print_flg,
  delivery_order_print_flg,
  house_card_display,
  house_card_validation,
  house_card_required,
  house_card_change,
  house_card_befor,
  register_no_max_length,
  receipt_no_max_length,
  register_no_validation,
  receipt_no_validation,
  memberNotesBefore,
  deliveryNotesBefore,
  messageMemberNotes,
  messageDeliveryNotes,
  is_delivery_address1_display,
  is_hide_delivery_time_on_spot,
  areaListItems,
  unitItems,
  originalAddress,
  prefSelectValue,
  cityBlockList,
  errdialog,
  errMessage,
}: UseDeliveryRequestInitParams) => {
  const initTableHeaders = (): void => {
    isPersonalInfoPossible.value = false;
    personalInfoImpossible.value = "";
    house_card_display.value = 0;
    house_card_validation.value = "";
    house_card_required.value = 0;
    house_card_change.value = false;
    house_card_befor.value = "";
    register_no_max_length.value = 0;
    register_no_validation.value = "";
    receipt_no_max_length.value = 0;
    receipt_no_validation.value = "";
    is_hide_delivery_time_on_spot.value = 0;
    memberNotesBefore.value = "";
    deliveryNotesBefore.value = "";
    messageMemberNotes.value = "";
    messageDeliveryNotes.value = "";
    authType.value = "";
    shipping_label_print_flg.value = false;
    delivery_order_print_flg.value = false;
    is_delivery_address1_display.value = 0;
    initializeTableHeaders(pageKey);
  };

  const setRegisterNoDisplay = (): boolean => register_no_max_length.value > 0;
  const setReceiptNoDisplay = (): boolean => receipt_no_max_length.value > 0;

  const runInitialLoad = async (): Promise<void> => {
    try {
      initTableHeaders();
      authType.value = localStorageGetAuthType();
      if (localStorageGetPersonalInfo() === PossibleType.possible) {
        isPersonalInfoPossible.value = true;
      } else {
        isPersonalInfoPossible.value = false;
        personalInfoImpossible.value = GeneralConstant.word_hide;
      }

      const wl = await getWordList(
        localStorageGetChainCode(),
        "ListDeliveryRequests",
        localStorageGetCustomerCode()
      );
      Object.assign(wordList, wl);

      if (chainViewFlag) {
        bufChainListItems.value = await getChainList(localStorageGetCustomerCode());
        searchConditionChainCode.value = [];
        shopListInput.chain_code = "";

        if (bufChainListItems.value && bufChainListItems.value.length > 0) {
          chainListItems.value = [];
          chainAllCheckFlag.value = "all";
          bufChainListItems.value.forEach((chainValue) => {
            if (!chainValue) {
              return;
            }
            chainListItems.value.push({
              chain_code: chainValue.chain_code,
              name: chainValue.name,
            });
            chainSelectValues.value.push({
              chain_code: chainValue.chain_code,
              name: chainValue.name,
            });
            searchConditionChainCode.value.push(chainValue.chain_code);
          });

          if (bufChainListItems.value.length === 1) {
            searchConditionChainCode.value = [localStorageGetChainCode()];
            shopListInput.chain_code = localStorageGetChainCode();
            bufShopListItems.value = await getShopList(shopListInput);

            shopAllCheckFlag.value = "none";
            shopDisabledFlag.value = false;
            shopSelectValues.value = [];
            searchConditionShopCode.value = [];

            if (bufShopListItems.value && bufShopListItems.value.length > 0) {
              shopListItems.value = [];
              bufShopListItems.value.forEach((shopValue) => {
                if (!shopValue) {
                  return;
                }
                shopListItems.value.push({
                  shop_code: shopValue.shop_code,
                  name: shopValue.name,
                });
              });
            }

            areaDisabledFlag.value = authType.value === "0" ? false : true;
          }

          shipping_label_print_flg.value = shopStore.getShippingLabelPrintFlg;
          delivery_order_print_flg.value = shopStore.getDeliveryOrderPrintFlg;
        }

        if (chainSelectValues.value.length === 1) {
          shopListInput.chain_code = chainSelectValues.value[0].chain_code;
          bufShopListItems.value = await getShopList(shopListInput);

          shopSelectValues.value = [];
          searchConditionShopCode.value = [];
          shopAllCheckFlag.value = "none";
          shopDisabledFlag.value = false;

          if (bufShopListItems.value && bufShopListItems.value.length > 0) {
            shopListItems.value = [];
            bufShopListItems.value.forEach((shopValue) => {
              if (!shopValue) {
                return;
              }
              shopListItems.value.push({
                shop_code: shopValue.shop_code,
                name: shopValue.name,
              });
            });
          }
          areaDisabledFlag.value = authType.value === "0" ? false : true;
        } else {
          shopAllCheckFlag.value = "none";
          shopDisabledFlag.value = true;
          shopSelectValues.value = [];
          searchConditionShopCode.value = [];
          shopListItems.value = [];
          areaDisabledFlag.value = true;
        }
      } else {
        // 本部権限・店舗権限の場合（chainViewFlag が false）の初期化処理
        // ログインユーザのチェーンコードを検索条件に設定する
        chainSelectValues.value.push({
          chain_code: localStorageGetChainCode(),
          name: "",
        });
        searchConditionChainCode.value = [localStorageGetChainCode()];
        shopListInput.chain_code = localStorageGetChainCode();

        // 店舗一覧を取得し、選択肢として設定する
        bufShopListItems.value = await getShopList(shopListInput);
        shopDisabledFlag.value = false;
        shopSelectValues.value = [];
        searchConditionShopCode.value = [];

        if (bufShopListItems.value && bufShopListItems.value.length > 0) {
          // 取得した店舗一覧をセレクトボックスの選択肢に追加する
          shopListItems.value = [];
          bufShopListItems.value.forEach((shopValue) => {
            if (!shopValue) {
              return;
            }
            shopListItems.value.push({
              shop_code: shopValue.shop_code,
              name: shopValue.name,
            });
          });

          // 本部権限（group=1）の場合、初期表示時にすべての店舗を選択済みにする
          // 店舗権限（group=0）の場合は未選択のままとする
          if (localStorageGetGroup() === "1") {
            // 全選択チェックを ON にし、全店舗を選択値・検索条件に追加する
            shopAllCheckFlag.value = "all";
            bufShopListItems.value.forEach((shopValue) => {
              if (!shopValue) {
                return;
              }
              shopSelectValues.value.push({
                shop_code: shopValue.shop_code,
                name: shopValue.name,
              });
              (searchConditionShopCode.value as string[]).push(shopValue.shop_code);
            });
          } else {
            // 店舗権限の場合はデフォルト未選択
            shopAllCheckFlag.value = "none";
          }
        } else {
          // 店舗一覧が空の場合はデフォルト未選択
          shopAllCheckFlag.value = "none";
        }

        areaDisabledFlag.value = authType.value === "0" ? false : true;

        const result = await getChainInfo(
          localStorageGetCustomerCode(),
          localStorageGetChainCode()
        );
        if (result) {
          house_card_display.value = result.house_card_display ? result.house_card_display : 0;
          house_card_validation.value = result.house_card_validation ? result.house_card_validation : "";
          if (
            house_card_validation.value !== "" &&
            house_card_validation.value.indexOf("required") !== -1
          ) {
            house_card_required.value = 1;
          }
          register_no_max_length.value = result.register_no_max_length ? result.register_no_max_length : 0;
          receipt_no_max_length.value = result.receipt_no_max_length ? result.receipt_no_max_length : 0;
          register_no_validation.value = `numeric|max:${result.register_no_max_length ? result.register_no_max_length.toString() : "0"}`;
          receipt_no_validation.value = `numeric|max:${result.receipt_no_max_length ? result.receipt_no_max_length.toString() : "0"}`;
          is_hide_delivery_time_on_spot.value = result.is_hide_delivery_time_on_spot
            ? result.is_hide_delivery_time_on_spot
            : 0;
        }
        shipping_label_print_flg.value = shopStore.getShippingLabelPrintFlg;
        delivery_order_print_flg.value = shopStore.getDeliveryOrderPrintFlg;
      }

      const initialShopCode = localStorageGetShopCode();
      if (initialShopCode) {
        const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
          await (await generateApolloClient()).query<
            ListDeliveryAreaQuery,
            ListDeliveryAreaQueryVariables
          >({
            query: ListDeliveryAreaDocument,
            variables: {
              chain_code: localStorageGetChainCode(),
              customer_code: localStorageGetCustomerCode(),
              shop_code: initialShopCode,
            },
          });
        areaListItems.value = listArea.data.listDeliveryArea;
      } else {
        areaListItems.value = [];
      }

      const maxUnit: ApolloQueryResult<GetChainQuery> =
        await (await generateApolloClient()).query<GetChainQuery>({
          query: GetChainDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
          },
        });
      for (let i = 0; i < Number(maxUnit.data.getChain!.max_unit); i++) {
        unitItems.value.push(i + 1);
      }

      originalAddress.value = prefSelectValue.value;
      if (prefSelectValue.value !== "" && initialShopCode) {
        cityBlockList.value = await getCityByPrefGroupedByStepA(
          localStorageGetCustomerCode(),
          localStorageGetChainCode(),
          initialShopCode,
          prefSelectValue.value
        );
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "エラーが発生しました。";
    }
  };

  return { runInitialLoad, setRegisterNoDisplay, setReceiptNoDisplay };
};
